import { HttpClient, HttpContext } from '@angular/common/http'
import { Injectable } from '@angular/core'
import {
  RootState,
  User,
  selectCurrentUser,
  selectClientId,
} from '@helios/state-management'
import {
  HANDLE_ERROR_AND_THROW,
  IGNORE_ERROR,
  isNullOrEmpty,
  stringToBase32,
} from '@helios/utils'
import { Store } from '@ngrx/store'
import { Subject } from 'rxjs'
import {
  delay,
  filter,
  map,
  retry,
  retryWhen,
  take,
  takeWhile,
} from 'rxjs/operators'

export interface FolderContents {
  objectPrefixes: string[]
  foldersPrefixes: string[]
}

export const STORAGE_URL = 'helios-ui-storage'
const headers = {
  headers: {
    accept: '*/*',
    'Cache-Control': 'no-cache',
    Pragma: 'no-cache',
    Expires: '0',
  },
  context: new HttpContext().set(HANDLE_ERROR_AND_THROW, true),
}

@Injectable({
  providedIn: 'root',
})
export class UiStorageService {
  private readonly base32User = this.store
    .select(selectCurrentUser)
    .pipe(
      takeWhile(user => user == null, true),
      filter((user): user is User => user != null),
      map(user =>
        stringToBase32(`${user.auth0Id}@${user.clearwaterData?.realm}`),
      ),
    )
    .toPromise()

  private readonly clientId = this.store
    .select(selectClientId)
    .pipe(
      takeWhile(clientId => clientId == null, true),
      filter((clientId): clientId is number => clientId != null),
    )
    .toPromise()

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<RootState>,
  ) {}

  getBase32User(): Promise<string> {
    return this.base32User
  }

  getClientId(): Promise<number> {
    return this.clientId
  }

  async getFolderContents(prefix?: string): Promise<FolderContents> {
    let params
    if (prefix != null)
      params = {
        prefix,
      }

    const subPrefixes: string[] = await this.http
      .get(STORAGE_URL, {
        ...headers,
        params,
      })
      .pipe(
        // Retry when the call fails, but only retry 3 times
        retryWhen(errors => errors.pipe(take(3), retry(3))),
        take(1),
        map(retVal => retVal as string[]),
      )
      .toPromise()

    const contents: FolderContents = {
      objectPrefixes: [],
      foldersPrefixes: [],
    }

    for (const subPrefix of subPrefixes) {
      if (subPrefix === prefix) continue
      if (subPrefix.endsWith('/'))
        contents.foldersPrefixes.push(
          subPrefix.substring(0, subPrefix.length - 1),
        )
      else contents.objectPrefixes.push(subPrefix)
    }

    return contents
  }

  async deleteObject(prefix: string): Promise<void> {
    await this.http
      .delete<void>(this.getUrl(prefix), headers)
      .pipe(take(1))
      .toPromise()
      .catch(() => undefined)
    storageUpdate$.next(prefix) //TODO temporary solution until we get an SSE PVAL-12762
  }

  /**
   * Deletes Folder and all children given the folders prefix. Filter groups and Workspaces top level is a folder.
   *
   * @param prefix
   */
  async deletePrefix(prefix = ''): Promise<void> {
    if (isNullOrEmpty(prefix)) return
    const prefixContents: FolderContents = await this.getFolderContents(prefix)
    prefixContents.objectPrefixes.forEach(o => this.deleteObject(o))
    prefixContents.foldersPrefixes.forEach(f => this.deletePrefix(f))
  }

  async getObject<T>(
    prefix: string,
    allowMissing = false,
  ): Promise<T | undefined> {
    return this.http
      .get<T>(
        this.getUrl(prefix),
        allowMissing
          ? {
              ...headers,
              context: new HttpContext()
                .set(HANDLE_ERROR_AND_THROW, true)
                .set(IGNORE_ERROR, [404]),
            }
          : headers,
      )
      .pipe(
        /**
         * It does not make sense for us to have an object prefix and for an item not to exist.
         * Retry up to 10 times when the call fails.
         * Delay retries between 50-150ms
         */
        retryWhen(errors =>
          errors.pipe(take(10), delay(50 + 100 * Math.random()), retry(10)),
        ),
        take(1),
      )
      .toPromise()
  }

  async putObject(prefix: string, o: unknown): Promise<void> {
    await this.http
      .put<void>(this.getUrl(prefix), o, headers)
      .pipe(take(1))
      .toPromise()
      .catch(() => undefined)
    storageUpdate$.next(prefix) //TODO temporary solution until we get an SSE PVAL-12762
  }

  private getUrl(prefix: string | null) {
    if (isNullOrEmpty(prefix)) return STORAGE_URL
    return `${STORAGE_URL}/${prefix}`
  }
}

//TODO temporary solution until we get an SSE PVAL-12762
export const storageUpdate$ = new Subject<string>()
